import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { BREAKPOINTS } from "@/constants/breakpoints";
import { debounce } from "@/utils/utils";

// Types
type DeviceType = "mobile" | "tablet" | "desktop";

interface BreakpointContextValue {
  device: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

// Context
const BreakpointContext = createContext<BreakpointContextValue | undefined>(undefined);

// Logic
const getDeviceType = (width: number): DeviceType => {
  if (width < BREAKPOINTS.md) {
    return "mobile";
  }
  if (width >= BREAKPOINTS.md && width < BREAKPOINTS.lg) {
    return "tablet";
  }
  return "desktop";
};

// Provider
export const BreakpointProvider = ({ children }: { children: ReactNode }) => {
  const [device, setDevice] = useState<DeviceType>(() => getDeviceType(window.innerWidth));

  const handleResize = useCallback(() => {
    const newDevice = getDeviceType(window.innerWidth);

    // 使用函數式更新，避免閉包問題依賴舊的 device
    setDevice((prevDevice) => {
      if (newDevice !== prevDevice) {
        return newDevice;
      }
      return prevDevice;
    });
  }, []);

  useEffect(() => {
    const debounceHandleResize = debounce(handleResize, 200);

    handleResize();
    window.addEventListener("resize", debounceHandleResize);

    return () => {
      window.removeEventListener("resize", debounceHandleResize);
      debounceHandleResize.clear();
    };
  }, [handleResize]);

  const contextValue: BreakpointContextValue = useMemo(
    () => ({
      device,
      isMobile: device === "mobile",
      isTablet: device === "tablet",
      isDesktop: device === "desktop",
    }),
    [device],
  );

  return <BreakpointContext.Provider value={contextValue}>{children}</BreakpointContext.Provider>;
};

// Hook
export const useBreakpointContext = () => {
  const context = useContext(BreakpointContext);
  if (context === undefined) {
    throw new Error("useBreakpointContext 必須在 BreakpointProvider 內使用");
  }
  return context;
};
